export type ChannelHandleValidationError =
  | 'required'
  | 'length'
  | 'charset'
  | 'start_letter'
  | 'end_special'
  | 'consecutive_periods'
  | 'phone_like'
  | 'url_like';

const ALLOWED_CHARS = /^[A-Za-z0-9_.-]+$/;
const STARTS_WITH_LETTER = /^[A-Za-z]/;
const ENDS_WITH_SPECIAL = /[_.-]$/;
const CONSECUTIVE_PERIODS = /\.\./;
const PHONE_LIKE = /\d{7,}/;
const URL_LIKE = /(https?:\/\/|www\.|\.(com|net|org|io|co|gov|edu|info|xyz|biz)(\b|$))/i;

/**
 * Strip a leading @ if the user typed one. Preserves original casing.
 */
export const normalizeChannelHandle = (raw: string): string => {
  return raw.trim().replace(/^@+/, '');
};

/**
 * Ensure a handle is displayed/sent with a leading @.
 */
export const withChannelHandlePrefix = (handle: string): string => {
  const normalized = normalizeChannelHandle(handle);
  return normalized ? `@${normalized}` : '';
};

export const validateChannelHandle = (rawHandle: string): ChannelHandleValidationError | null => {
  const handle = normalizeChannelHandle(rawHandle);

  if (!handle) {
    return 'required';
  }

  if (handle.length < 3 || handle.length > 30) {
    return 'length';
  }

  if (!ALLOWED_CHARS.test(handle)) {
    return 'charset';
  }

  if (!STARTS_WITH_LETTER.test(handle)) {
    return 'start_letter';
  }

  if (ENDS_WITH_SPECIAL.test(handle)) {
    return 'end_special';
  }

  if (CONSECUTIVE_PERIODS.test(handle)) {
    return 'consecutive_periods';
  }

  if (PHONE_LIKE.test(handle)) {
    return 'phone_like';
  }

  if (URL_LIKE.test(handle)) {
    return 'url_like';
  }

  return null;
};

export const isValidChannelHandleFormat = (handle: string): boolean => {
  return validateChannelHandle(handle) === null;
};
